<!--
    AI 推荐值结果展示 + 采纳组件
    展示：一致/不一致标签 + AI推荐值文字 + 采纳按钮
    根据 isConsistent 判断：0=不一致，1=一致
    recommendedValues 为 JSON 字符串数组，取 value（+ unit 如果有值）展示
    点击采纳 emit adopt 事件，携带 { attrId, recommendedValues }
-->
<template>
    <div
        v-if="parsedList.length || curAttrRejectInfo"
        class="recommend-value-result"
        :class="{
            consistent: !inconsistent && !curAttrRejectInfo && showRecommendTextTitle,
            inconsistent: inconsistent,
            rejection: curAttrRejectInfo,
        }"
    >
        <!-- 标签 -->
        <mc-tag
            v-if="consistent && !curAttrRejectInfo"
            theme="green"
            type="pure"
            class="tag"
        >
            与 AI 推荐一致
        </mc-tag>
        <mc-tag
            v-if="inconsistent && !curAttrRejectInfo"
            theme="red"
            type="pure"
            class="tag"
        >
            与 AI 推荐不一致
        </mc-tag>
        <mc-tag
            v-if="curAttrRejectInfo"
            theme="red"
            type="pure"
            class="tag"
        >
            已驳回
        </mc-tag>

        <!-- 推荐值文字 + 采纳按钮 -->
        <template v-if="displayText">
            <mc-tooltip
                v-if="isDisplayTextOverflow"
                :content="displayText"
                theme="light"
                placement="top"
            >
                <span class="recommend-text">
                    <span v-if="title">{{ title }}: </span>
                    {{ truncatedDisplayText }}
                </span>
            </mc-tooltip>
            <span
                v-else
                class="recommend-text"
            >
                <span v-if="title">{{ title }}: </span>
                {{ displayText }}
            </span>
            <mc-button
                v-if="showAdoptBtn && parsedList.length"
                size="small"
                class="adopt-btn"
                @click="handleAdopt"
            >
                采纳
            </mc-button>
        </template>
    </div>
</template>

<script>
import {
    ATTR_INPUT_TYPE_ENUM, CONSISTENT_ENUM, parseRecommendValue, parseRecommendedValuesList,
} from 'utils/recommendMap';

export default {
    name: 'RecommendValueResult',
    props: {
        // 属性 ID（取自 ATTR_ID_ENUM）
        attrId: {
            type: [String, Number],
            required: true,
        },
        // 属性类型
        attrInputType: {
            type: Number,
            default: ATTR_INPUT_TYPE_ENUM.TEXT_INPUT,
        },
        // 是否展示推荐值title
        showRecommendTextTitle: {
            type: Boolean,
            default: true,
        },
        // AI推荐值 or 审核驳回推荐值（JSON 字符串，如 "[{'value':'冷冻','id':'1','unit': null}]"）
        recommendedValues: {
            type: String,
            default: '',
        },
        // AI校验是否 是否一致：0=不一致，1=一致
        isConsistent: {
            type: Number,
            default: null,
        },
        // 驳回信息
        mergedRejectItems: {
            type: Object,
            default: () => ({}),
        },
        // 是否展示采纳按钮
        showAdoptBtn: {
            type: Boolean,
            default: true,
        },
    },
    computed: {
        // 是否一致
        consistent() {
            return this.isConsistent === CONSISTENT_ENUM.CONSISTENT;
        },
        inconsistent() {
            return this.isConsistent === CONSISTENT_ENUM.INCONSISTENT;
        },
        // 解析 recommendedValues JSON 字符串 → 数组（单引号处理由 parseRecommendedValuesList 统一负责）
        parsedList() {
            return parseRecommendedValuesList(this.recommendedValues);
        },
        // 如果是驳回组件，则取当前属性驳回信息
        curAttrRejectInfo() {
            return this.mergedRejectItems[this.attrId];
        },

        isMulti() {
            return this.attrInputType === ATTR_INPUT_TYPE_ENUM.MULTI_SELECT;
        },
        // displayText 是否超过 20 个字符
        isDisplayTextOverflow() {
            return this.displayText.length > 20;
        },
        // 超过 20 个字符截断加省略号，否则原样返回
        truncatedDisplayText() {
            if (!this.isDisplayTextOverflow) return this.displayText;
            return `${this.displayText.slice(0, 20)}...`;
        },

        // 标题：
        //   无驳回信息（纯 AI 推荐态）→ 'AI推荐'
        //   有驳回信息：
        //     ① recommendedValues 有值（驳回推荐值）→ '驳回推荐'
        //     ② remark 有值 / ④ comments（驳回原因）→ '驳回原因'
        //     ③ AI推荐值兜底 → 'AI推荐'
        title() {
            if (!this.showRecommendTextTitle) {
                return '';
            }
            if (!this.curAttrRejectInfo) {
                return 'AI 推荐';
            }
            // ① 驳回推荐值
            if (this.curAttrRejectInfo?.recommendedValues) {
                return '审核推荐';
            }
            // ② 备注 或 ④ 问题描述 → 驳回原因
            if (this.curAttrRejectInfo?.remark) {
                return '驳回原因';
            }
            // ③ AI推荐值兜底
            if (this.parsedList.length) {
                return 'AI推荐';
            }
            // ④ 问题描述
            return '驳回原因';
        },

        // 展示文本：
        //   驳回状态（curAttrRejectInfo 有值）按以下优先级取值：
        //     1. 有驳回推荐值（curAttrRejectInfo.AIRecommend，JSON字符串，格式同 recommendedValues）→ 解析后展示
        //     2. 有备注（curAttrRejectInfo.remark）→ 展示备注
        //     3. 有AI推荐值（parsedList）→ 展示AI推荐值
        //     4. 兜底 → 展示问题描述（comments.map(item=>item.name).join('，')）
        //   非驳回状态：
        //     多选 → 所有项的 value 用「、」拼接
        //     单值 → 第一项 value（+ unit 有值则拼接，如"1天"）
        displayText() {
            if (this.curAttrRejectInfo) {
                // ① 驳回推荐值（AIRecommend 是 JSON 字符串，使用驳回推荐值）
                if (this.curAttrRejectInfo?.recommendedValues) {
                    return this._formatParsedList();
                }
                // ② 备注
                if (this.curAttrRejectInfo?.remark) {
                    return this.curAttrRejectInfo.remark;
                }
                // ③ AI推荐值
                if (this.parsedList.length) {
                    return this._formatParsedList();
                }
                // ④ 问题描述
                const comments = this.curAttrRejectInfo.comments;
                if (Array.isArray(comments) && comments.length) {
                    return comments.map((item) => item.name).join('，');
                }
                return '';
            }
            // 非驳回：直接展示 AI 推荐值
            if (!this.parsedList.length) return '';
            return this._formatParsedList();
        },
    },
    methods: {
        // 将任意 [{value, unit}] 数组格式化为展示文本（多选用「、」拼接，单值拼接 unit）
        _formatParsedList() {
            return parseRecommendValue({
                attrId: this.attrId,
                attrInputType: this.attrInputType,
                recommendedValues: this.recommendedValues,
            });
        },
        handleAdopt() {
            this.$emit('adopt', {
                attrId: this.attrId,
                attrInputType: this.attrInputType,
                recommendedValues: this.recommendedValues,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.recommend-value-result {
    height: 32px;
    border-radius: 4px;
    margin-top: 4px;
    display: flex;
    width: fit-content;
    flex-direction: row;
    align-items: center;
    padding: 4px;
    gap: 8px;

    color: #111925;

    .tag {
        cursor: default;
        flex-shrink: 0;
        border-radius: 4px;
        padding: 1px 4px;
    }

    .recommend-text {
        font-size: 12px;
    }

    .adopt-btn {
        padding: 0;
        font-size: 12px;
        height: 20px;
        line-height: 20px;
        ::v-deep .mtd-btn-small{
            height: 20px;
            &>span {
                line-height: 20px;
            }
        }
    }
}
.consistent {
    background: #F0FAF6;
}
.inconsistent {
    background: #FFF6F0;
}
.rejection {
    background: #FFF2F2;
}
</style>
